import React from 'react'

import {
  Box,
  Checkbox,
  TableHead,
  TableSortLabel,
  Tooltip
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'

import { stickyEndCellSx } from './stickyCell'
import { HeadCell, Order } from './Table.interface'
import {
  TableCell as EnhancedTableCell,
  TableRow as EnhancedTableRow
} from './Table.styles'

const isOverInfoHint = (target: EventTarget | null) =>
  target instanceof Element && !!target.closest('[data-info-hint]')

/**
 * Tooltip de ordenação do cabeçalho ("Ordenar crescente"...). Ele envolve o rótulo
 * inteiro, e um cabeçalho com (i) (InfoHint) mostrava os dois tooltips ao mesmo tempo.
 * Controlado para não abrir, e fechar se já estiver aberto, com o mouse sobre o (i).
 */
const SortTooltip = ({
  title,
  children
}: {
  title: string
  children: React.ReactElement
}) => {
  const [open, setOpen] = React.useState(false)

  return (
    <Tooltip
      title={title}
      arrow
      placement="top"
      open={open}
      onOpen={(e) => setOpen(!isOverInfoHint(e.target))}
      onClose={() => setOpen(false)}
    >
      {React.cloneElement(children, {
        onMouseOver: (e: React.MouseEvent) => {
          if (isOverInfoHint(e.target)) setOpen(false)
          children.props.onMouseOver?.(e)
        }
      })}
    </Tooltip>
  )
}

export interface SortConfig {
  field: string
  direction: Order
}

export type MultiSortConfig = SortConfig[]

export interface SortableColumn {
  /** Column ID that matches the backend sort field */
  id: string
  /** Whether this column is sortable */
  sortable?: boolean
  /** Custom sort field if different from column ID */
  sortField?: string
}

interface SortableTableHeadProps {
  /** Table header cells */
  header: HeadCell[]
  /** Current sort configuration (single or multi-column) */
  sortConfig?: SortConfig | MultiSortConfig
  /** Callback when sort is requested - receives the new sort configuration */
  onSort?: (field: string, direction: Order) => void
  /** Callback for multi-column sort - receives the full array of sorts */
  onMultiSort?: (sorts: MultiSortConfig) => void
  /** Configuration for sortable columns */
  sortableColumns?: SortableColumn[]
  /** Whether to use selection checkboxes */
  useSelect?: boolean
  /** Number of selected rows */
  numSelected?: number
  /** Total row count */
  rowCount?: number
  /** Callback for select all */
  onSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>) => void
  /** Disable all sorting */
  disableSort?: boolean
  /** Enable multi-column sorting */
  multiSort?: boolean
  /** Enable single selection mode (radio buttons) */
  singleSelect?: boolean
  /** Column ids pinned to the right edge while scrolling horizontally. */
  stickyEndColumns?: string[]
}

const SortableTableHead: React.FC<SortableTableHeadProps> = ({
  header,
  sortConfig,
  onSort,
  onMultiSort,
  sortableColumns = [],
  useSelect = false,
  numSelected = 0,
  rowCount = 0,
  onSelectAllClick,
  disableSort = false,
  multiSort = false,
  singleSelect = false,
  stickyEndColumns = []
}) => {
  const stickyEndSet = new Set(stickyEndColumns)
  // Create a map for quick lookup of sortable columns
  const sortableMap = new Map<string, SortableColumn>()
  sortableColumns.forEach((col) => {
    sortableMap.set(col.id, col)
  })

  // Normalize sortConfig to always work with arrays internally
  const sortArray: MultiSortConfig = Array.isArray(sortConfig)
    ? sortConfig
    : sortConfig?.field
    ? [sortConfig]
    : []

  const handleSortClick = (columnId: string) => {
    if (disableSort) return

    const sortableCol = sortableMap.get(columnId)
    if (!sortableCol?.sortable) return

    const sortField = sortableCol.sortField || columnId
    const existingIndex = sortArray.findIndex((s) => s.field === sortField)
    const existingSort = existingIndex >= 0 ? sortArray[existingIndex] : null

    if (onMultiSort) {
      // Multi-column sorting logic (also handles single column when multiSort=false)
      let newSorts: MultiSortConfig

      if (!existingSort) {
        // Add new sort (or replace if not multiSort)
        newSorts = multiSort
          ? [...sortArray, { field: sortField, direction: 'asc' }]
          : [{ field: sortField, direction: 'asc' }]
      } else if (existingSort.direction === 'asc') {
        // Toggle to descending
        newSorts = sortArray.map((s, i) =>
          i === existingIndex ? { ...s, direction: 'desc' as const } : s
        )
      } else {
        // Remove this sort
        newSorts = sortArray.filter((_, i) => i !== existingIndex)
      }

      onMultiSort(newSorts)
    } else if (onSort) {
      // Single column sorting logic (backwards compatible)
      let newDirection: Order

      if (!existingSort) {
        newDirection = 'asc'
      } else if (existingSort.direction === 'asc') {
        newDirection = 'desc'
      } else {
        newDirection = undefined
      }

      onSort(sortField, newDirection)
    }
  }

  const getSortDirection = (columnId: string): Order => {
    const sortableCol = sortableMap.get(columnId)
    if (!sortableCol) return undefined

    const sortField = sortableCol.sortField || columnId
    const sort = sortArray.find((s) => s.field === sortField)
    return sort?.direction
  }

  const getSortIndex = (columnId: string): number => {
    if (!multiSort || sortArray.length <= 1) return -1

    const sortableCol = sortableMap.get(columnId)
    if (!sortableCol) return -1

    const sortField = sortableCol.sortField || columnId
    return sortArray.findIndex((s) => s.field === sortField)
  }

  const isColumnSortable = (columnId: string): boolean => {
    if (disableSort) return false
    const sortableCol = sortableMap.get(columnId)
    return sortableCol?.sortable ?? false
  }

  return (
    <TableHead>
      <EnhancedTableRow>
        {useSelect && (
          <EnhancedTableCell padding="checkbox">
            {!singleSelect && (
              <Checkbox
                color="primary"
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={onSelectAllClick}
              />
            )}
          </EnhancedTableCell>
        )}
        {header.map((headCell) => {
          const sortable = isColumnSortable(headCell.id)
          const sortDirection = getSortDirection(headCell.id)
          const isSorted = sortDirection !== undefined

          const isSticky = stickyEndSet.has(headCell.id)
          const widthSx =
            headCell.width !== undefined ? { width: headCell.width } : undefined

          if (!sortable) {
            return (
              <EnhancedTableCell
                key={headCell.id}
                sx={isSticky ? stickyEndCellSx('head', widthSx) : widthSx}
              >
                {headCell.label}
              </EnhancedTableCell>
            )
          }

          const sortIndex = getSortIndex(headCell.id)

          return (
            <EnhancedTableCell
              key={headCell.id}
              sortDirection={sortDirection || false}
              sx={
                isSticky
                  ? stickyEndCellSx('head', { ...widthSx, cursor: 'pointer' })
                  : { ...widthSx, cursor: 'pointer' }
              }
            >
              <SortTooltip
                title={
                  isSorted
                    ? sortDirection === 'asc'
                      ? 'Ordenar decrescente'
                      : 'Remover ordenação'
                    : 'Ordenar crescente'
                }
              >
                <TableSortLabel
                  active={isSorted}
                  direction={sortDirection || 'asc'}
                  onClick={() => handleSortClick(headCell.id)}
                  sx={{
                    '&:hover': {
                      color: 'grey.700'
                    },
                    '&.Mui-active': {
                      color: 'grey.800',
                      '& .MuiTableSortLabel-icon': {
                        color: 'grey.600'
                      }
                    }
                  }}
                >
                  {headCell.label}
                  {sortIndex >= 0 && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: '10px',
                        ml: 0.5,
                        color: 'grey.500',
                        fontWeight: 'normal'
                      }}
                    >
                      {sortIndex + 1}
                    </Box>
                  )}
                  {isSorted && (
                    <Box component="span" sx={visuallyHidden}>
                      {sortDirection === 'desc'
                        ? 'ordenado decrescente'
                        : 'ordenado crescente'}
                    </Box>
                  )}
                </TableSortLabel>
              </SortTooltip>
            </EnhancedTableCell>
          )
        })}
      </EnhancedTableRow>
    </TableHead>
  )
}

export default SortableTableHead
